import * as React from "react"

import { cn } from "../../lib/utils"

export type InputProps = React.ComponentProps<"input"> & {
  label?: string,
  error?: string,
  iconOrTextPosition?: "left" | "right";
  iconOrText?: React.ReactNode | string;
  formatThousands?: boolean;
};

function formatNumberWithSpaces(value: string | number) {
  if (value === "" || value === null || value === undefined) return "";
  const num = typeof value === "number" ? value : Number(value.toString().replace(/\s/g, ""));
  if (isNaN(num)) return "";
  return num.toLocaleString("fr-FR").replace(/,/g, " ");
}

function parseNumberFromSpaces(value: string) {
  return value.replace(/\s/g, "");
}

function Input({ error, label, className, iconOrText, iconOrTextPosition, required, type, formatThousands, value, onChange, ...props }: InputProps) {
  const isFormatted = type === "number" && formatThousands;
  const [displayValue, setDisplayValue] = React.useState(
    isFormatted && typeof value === "number"
      ? formatNumberWithSpaces(value)
      : value ?? ""
  );

  React.useEffect(() => {
    if (isFormatted && typeof value === "number") {
      setDisplayValue(formatNumberWithSpaces(value));
    } else if (!isFormatted) {
      setDisplayValue((value as string) ?? "");
    }
  }, [value, isFormatted]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    if (isFormatted) {
      const raw = parseNumberFromSpaces(val);
      setDisplayValue(formatNumberWithSpaces(raw));
      if (onChange) {
        onChange({
          ...e,
          target: {
            ...e.target,
            value: raw,
          },
        });
      }
    } else {
      setDisplayValue(val);
      if (onChange) onChange(e);
    }
  };

  return (
    <div className="*:not-first:mt-2 w-full flex flex-col items-start">
      {label && (
        <p>
          {label} {required && <span className="text-destructive">*</span>}
        </p>
      )}
      <div className="relative w-full">
        <input
          data-slot="input"
          className={cn(
            "border-input file:text-foreground placeholder:text-muted-foreground/70 flex h-11 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-sm shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
            "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
            type === "search" &&
              "[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none [&::-webkit-search-results-button]:appearance-none [&::-webkit-search-results-decoration]:appearance-none",
            type === "file" &&
              "text-muted-foreground/70 file:border-input file:text-foreground p-0 pr-3 italic file:me-3 file:h-full file:border-0 file:border-r file:border-solid file:bg-transparent file:px-3 file:text-sm file:font-medium file:not-italic",
            type === "number" && "[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
            iconOrTextPosition === "left" && "ps-9",
            iconOrTextPosition === "right" && "pe-9",
            className,
          )}
          aria-invalid={!!error}
          type={isFormatted ? "text" : type}
          value={displayValue}
          onChange={handleChange}
          {...props}
        />
        <div className={cn("text-muted-foreground/80 pointer-events-none absolute inset-y-0 flex items-center justify-center peer-disabled:opacity-50", iconOrTextPosition === "left" && "left-0 ps-3", iconOrTextPosition === "right" && "right-0 pe-3")}>
          {iconOrText}
        </div>
      </div>
      {error && (
        <p className="text-destructive mt-2 text-xs" role="alert" aria-live="polite">
          {error}
        </p>
      )}
    </div>
  )
}

export { Input }
